import React from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import { FilterBar } from 'components/FilterBar';
import { FilterButton } from 'containers/Eleves/List/Table/FilterBar/FilterButton';
import { AddFilterButton } from 'containers/Eleves/List/Table/FilterBar/AddFilterButton';
import { useFilterButtonRefs } from 'containers/Eleves/List/Table/FilterBar/useFilterButtonRefs';
import useCreateElevesViewFilter from 'queries/eleves/views/useCreateElevesViewFilter';
import useDeleteElevesViewFilter from 'queries/eleves/views/useDeleteElevesViewFilter';
import useUpdateElevesViewFilter from 'queries/eleves/views/useUpdateElevesViewFilter';
import { useTranslation } from 'react-i18next';
import { useIsOpenFilterBar } from 'containers/Eleves/List/Table/FilterBar/atom';
import { EleveGroup } from 'types/eleveGroups';
import { Eleve, ElevesView } from 'types/eleves';
import { Table } from '@tanstack/react-table';
import { Classroom } from 'types/classroom';
import { CustomFieldDefinition } from 'types/customField';
import { Filter } from './types';

interface Props {
  tableInstance: Table<Eleve>;
  view: ElevesView;
  ff: string[];
  classrooms: Classroom[];
  eleveGroups: EleveGroup[];
  customFields: CustomFieldDefinition[];
  getLabelBasicField: (id: string) => string;
}

export function FilterBarWrapper(props: Props) {
  const {
    tableInstance,
    view,
    ff,
    classrooms,
    eleveGroups,
    getLabelBasicField,
  } = props;
  const isOpen = useIsOpenFilterBar();
  const { t } = useTranslation(['common', 'eleve']);

  const translatedViewFields = view.fields.map(f => ({
    ...f,
    fieldName: getLabelBasicField(f.id),
  }));

  function getOptionsFromField(key: string) {
    if (key === 'gender') {
      return [
        {
          label: t('eleve:fields.boy'),
          value: 'H',
        },
        {
          label: t('eleve:fields.girl'),
          value: 'F',
        },
      ];
    }
    if (key === 'classroomId') {
      return classrooms.map(cl => ({ label: cl.name, value: cl.id }));
    }
    if (key === 'groups') {
      return eleveGroups.map(gg => ({
        label: gg.name,
        value: gg.id,
      }));
    }

    const matchingCustomField = props.customFields.find(c => c.id === key);
    if (matchingCustomField && matchingCustomField.type === 'select') {
      return (matchingCustomField?.options || []).map(opt => ({
        label: opt.value,
        value: opt.id,
      }));
    }
    return undefined;
  }

  const createFilter = useCreateElevesViewFilter(view.id);
  const deleteFilter = useDeleteElevesViewFilter(view.id);
  const updateFilter = useUpdateElevesViewFilter(view.id);
  const { setInputRef, getInputRef } = useFilterButtonRefs(ff);
  const filterOptions = tableInstance
    .getLeafHeaders()
    .filter(h => !h.column.getIsFiltered() && h.column.getCanFilter())
    .map(c => ({
      id: c.id,
      label: getLabelBasicField(c.id),
    }));
  if (!isOpen) {
    return null;
  }
  return (
    <FilterBar tw="mt-4 mb-0 sm:mt-0 sm:mb-2">
      {tableInstance.getState().columnFilters.map(c => {
        const col = tableInstance.getColumn(c.id);
        const filterObj: Filter = {
          fieldLabel: getLabelBasicField(c.id),
          filterValue: c.value as any,
          fieldType: (translatedViewFields.find(f => f.id === c.id)
            ?.fieldType || 'select') as any,
          options:
            getOptionsFromField(c.id) ||
            Array.from(col?.getFacetedUniqueValues()?.keys() || [])
              .sort()
              .filter(v => v)
              .map(v => ({ value: v, label: v })),
        };

        const filter = view.filters.filterSet.find(f => f.columnId === c.id);
        return (
          <FilterButton
            buttonRef={setInputRef(c.id)}
            onDelete={g => {
              col?.setFilterValue(g);
              if (!filter?.id) {
                return;
              }
              return deleteFilter(filter?.id);
            }}
            onChange={g => {
              col?.setFilterValue(g);
              if (!filter) {
                return;
              }
              return updateFilter({ ...filter, value: g });
            }}
            key={c.id}
            filter={filterObj}
          />
        );
      })}
      {filterOptions.length > 0 ? (
        <AddFilterButton
          onAdd={id => {
            const col = tableInstance.getColumn(id);
            const fieldType = translatedViewFields.find(
              f => f.id === id,
            )?.fieldType;
            let tableFilters: any = Array.from(
              col?.getFacetedUniqueValues()?.keys() || [],
            ).filter(v => v);

            if (fieldType === 'select') {
              // values are arrays so we need to flatten
              tableFilters = tableFilters.flat().filter(v => v);
            }
            if (fieldType === 'bool') {
              tableFilters = true;
            }
            if (fieldType === 'number') {
              const sorted = tableFilters.sort((a, b) => a - b);
              const min = sorted[0];
              const max = sorted[sorted.length - 1];
              tableFilters = [min ?? '', max ?? ''];
            }
            col?.setFilterValue(tableFilters);
            createFilter({
              id: uuid(),
              columnId: id,
              value: tableFilters,
              operator:
                tableInstance?.getColumn(id)?.columnDef.filterFn?.toString() ||
                '',
            }).then(() => {
              setTimeout(() => {
                const field = getInputRef(id);
                if (field) {
                  field.click();
                }
              }, 300);
            });
          }}
          headers={filterOptions}
        />
      ) : (
        <div tw="text-muted text-sm">
          {t('eleve:import.noFieldDisplayedCanBeFiltered')}
        </div>
      )}
    </FilterBar>
  );
}
